import React, { useEffect, useRef } from 'react';

interface DirectionsMapProps {
  destination: google.maps.LatLngLiteral | null;
  className?: string;
  onDirectionsCalculated?: (result: google.maps.DirectionsResult | null) => void;
  onMapInstance?: (map: google.maps.Map | null) => void;
  onStreetUpdate?: (street: string | null) => void;
  onStepUpdate?: (stepIndex: number) => void;
}

const DEFAULT_CENTER = { lat: 37.7749, lng: -122.4194 };

const DirectionsMap: React.FC<DirectionsMapProps> = ({
  destination,
  className = '',
  onDirectionsCalculated,
  onMapInstance,
  onStreetUpdate,
  onStepUpdate
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const directionsRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);

  // Initialize map when Google Maps is available
  useEffect(() => {
    if (!mapContainerRef.current || !window.google?.maps) return;

    const map = new google.maps.Map(mapContainerRef.current, {
      center: DEFAULT_CENTER,
      zoom: 12,
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true
    });

    mapRef.current = map;
    onMapInstance?.(map);

    return () => {
      directionsRendererRef.current?.setMap(null);
      directionsRendererRef.current = null;
      mapRef.current = null;
      onMapInstance?.(null);
    };
  }, [onMapInstance]);

  // Request and display directions when destination is set
  useEffect(() => {
    if (!destination || !window.google?.maps || !mapRef.current) {
      if (onDirectionsCalculated) onDirectionsCalculated(null);
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setMap(null);
        directionsRendererRef.current = null;
      }
      return;
    }

    const directionsService = new google.maps.DirectionsService();

    if (!directionsRendererRef.current) {
      directionsRendererRef.current = new google.maps.DirectionsRenderer({
        map: mapRef.current,
        suppressMarkers: false
      });
    } else {
      directionsRendererRef.current.setMap(mapRef.current);
    }

    const origin = DEFAULT_CENTER; // Could be replaced with current position from geolocation
    const request: google.maps.DirectionsRequest = {
      origin,
      destination,
      travelMode: google.maps.TravelMode.DRIVING
    };

    directionsService.route(request, (result, status) => {
      if (status === google.maps.DirectionsStatus.OK && result) {
        directionsRendererRef.current?.setDirections(result);
        onDirectionsCalculated?.(result);
        onStepUpdate?.(0);
      } else {
        onDirectionsCalculated?.(null);
      }
    });
  }, [destination, onDirectionsCalculated, onStepUpdate]);

  return <div ref={mapContainerRef} className={className} />;
};

export default DirectionsMap;
